import {
  Component,
  Inject,
  inject,
  Input,
  OnInit,
  Optional,
} from "@angular/core";
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import { CommonModule } from "@angular/common";
import { ActivatedRoute, Router } from "@angular/router";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { catchError, forkJoin, map, of, switchMap, tap } from "rxjs";

import { ApiService } from "./../../../shared/services/api/api.service";
import { API_ENDPOINTS } from "../../../shared/services/api/api-endpoints";

import { FormSectionComponent } from "../../../shared/form-section/form-section.component";
import { GloDatePickerComponent } from "../../../shared/components/glo-date-picker/glo-date-picker/glo-date-picker.component";
import { LoaderComponent } from "../../../shared/components/loader/loader.component";
import { GloPhonenumberInputComponent } from "../../../shared/components/glo-phonenumber-input/glo-phonenumber-input.component";

import { NgxIntlTelInputModule } from "ngx-intl-tel-input";
import {
  CountryISO,
  PhoneNumberFormat,
  SearchCountryField,
} from "ngx-intl-tel-input";

import { Salon } from "../../../shared/models/salon.model";
import { Employee } from "../../../shared/models/employee.model";
import { Service } from "../../../shared/models/service.model";

export interface EmployeeData {
  isEdit: boolean;
  employee?: Employee;
  employee_id?: string;
}

interface StaffServiceMapping {
  id: string;
  service: string;
  service_id?: string;
}

type EmployeeFormTab =
  | "profile"
  | "address"
  | "employment"
  | "services"
  | "availability";

@Component({
  selector: "app-add-employee",
  standalone: true,
  imports: [
    FormSectionComponent,
    GloDatePickerComponent,
    CommonModule,
    ReactiveFormsModule,
    GloPhonenumberInputComponent,
    NgxIntlTelInputModule,
    LoaderComponent,
  ],
  templateUrl: "./add-employee.component.html",
  styleUrl: "./add-employee.component.scss",
})
export class AddEmployeeComponent implements OnInit {
  @Input() data!: EmployeeData;

  private fb = inject(FormBuilder);
  private apiService = inject(ApiService);
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);

  employeeForm: FormGroup;
  loading = false;

  employeeID: string | null = null;
  salons: Salon[] = [];
  services: Service[] = [];
  loadingServices = false;

  activeTab: EmployeeFormTab = "profile";

  tabs: { key: EmployeeFormTab; label: string }[] = [
    { key: "profile", label: "Profile" },
    { key: "address", label: "Address" },
    { key: "employment", label: "Employment" },
    { key: "services", label: "Services" },
    { key: "availability", label: "Availability" },
  ];

  inputClasses =
    "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black";

  separateDialCode = true;
  SearchCountryField = SearchCountryField;
  CountryISO = CountryISO;
  PhoneNumberFormat = PhoneNumberFormat;

  preferredCountries: CountryISO[] = [
    CountryISO.UnitedStates,
    CountryISO.UnitedKingdom,
  ];

  customCssClass = "form-control rounded-[8px] w-full block";

  passwordFieldType: "password" | "text" = "password";

  readonly workingDays = [
    { label: "Monday", value: 0 },
    { label: "Tuesday", value: 1 },
    { label: "Wednesday", value: 2 },
    { label: "Thursday", value: 3 },
    { label: "Friday", value: 4 },
    { label: "Saturday", value: 5 },
    { label: "Sunday", value: 6 },
  ];

  constructor(
    @Optional() @Inject(MAT_DIALOG_DATA) public dialogData: EmployeeData | null,
    @Optional() private dialogRef: MatDialogRef<AddEmployeeComponent> | null,
  ) {
    this.employeeID =
      this.dialogData?.employee_id ||
      this.resolveStaffUserId(this.dialogData?.employee) ||
      this.activatedRoute.snapshot.paramMap.get("id");

    this.employeeForm = this.fb.group({
      full_name: ["dummy", [Validators.required]],
      password: [""],
      salon_id: ["", Validators.required],

      firstName: ["", Validators.required],
      lastName: ["", Validators.required],
      dateOfBirth: ["", Validators.required],
      gender: ["", Validators.required],
      maritalStatus: ["", Validators.required],
      city: ["", Validators.required],

      address: ["", Validators.required],
      nationality: ["", Validators.required],
      province: ["", Validators.required],
      postalCode: ["", Validators.required],
      addressCity: ["", Validators.required],

      phone_number: ["", Validators.required],
      email: ["", [Validators.required, Validators.email]],

      job_title: ["", Validators.required],
      departmentName: ["", Validators.required],
      interviewDate: ["", Validators.required],
      hiringDate: ["", Validators.required],
      designation: ["", Validators.required],
      grossSalary: ["", Validators.required],
      reportingTo: ["", Validators.required],

      selected_service_ids: this.fb.control<string[]>([], Validators.required),
      working_hours: this.fb.array([]),
    });

    this.buildWorkingHours();
  }

  ngOnInit(): void {
    if (!this.isEditMode) {
      this.employeeForm
        .get("password")
        ?.setValidators([Validators.required, Validators.minLength(8)]);
      this.employeeForm.get("password")?.updateValueAndValidity();
    }

    this.loadSalons();

    this.employeeForm.get("salon_id")?.valueChanges.subscribe((salonId) => {
      if (salonId) {
        this.loadServicesForSalon(salonId);
      } else {
        this.services = [];
        this.employeeForm.patchValue({ selected_service_ids: [] });
      }
    });

    if (this.dialogData?.employee) {
      this.populateForm(this.dialogData.employee);
    }

    if (this.employeeID) {
      this.loadEmployee();
    }
  }

  get isEditMode(): boolean {
    return !!this.employeeID || !!this.dialogData?.isEdit;
  }

  get modalTitle(): string {
    return this.isEditMode ? "Edit Employee" : "Add Employee";
  }

  get workingHoursArray(): FormArray {
    return this.employeeForm.get("working_hours") as FormArray;
  }

  get employeeDisplayName(): string {
    const firstName = this.employeeForm.get("firstName")?.value || "";
    const lastName = this.employeeForm.get("lastName")?.value || "";
    return `${firstName} ${lastName}`.trim();
  }

  get employeeInitials(): string {
    const name = this.employeeDisplayName || "Employee";

    return name
      .split(" ")
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part.charAt(0))
      .join("")
      .toUpperCase();
  }

  get selectedSalonName(): string {
    const salonId = this.employeeForm.get("salon_id")?.value;

    if (!salonId) return "Not selected";

    return (
      this.salons.find((salon) => salon.salon_id === salonId)?.name ||
      "Selected"
    );
  }

  get selectedServiceCount(): number {
    return this.employeeForm.get("selected_service_ids")?.value?.length || 0;
  }

  get enabledWorkingDaysCount(): number {
    return this.workingHoursArray.controls.filter(
      (ctrl) => !!ctrl.get("enabled")?.value,
    ).length;
  }

  togglePasswordVisibility(): void {
    this.passwordFieldType =
      this.passwordFieldType === "password" ? "text" : "password";
  }

  validationChecker(): boolean {
    return !!(
      this.employeeForm.get("phone_number")?.touched &&
      this.employeeForm.get("phone_number")?.invalid
    );
  }

  fetchInputField(controlName: string): FormControl {
    return this.employeeForm.get(controlName) as FormControl;
  }

  checkValidField(fieldName: string): boolean {
    const formField = this.employeeForm.get(fieldName);

    if (!formField) return true;

    return !(formField.invalid && formField.touched);
  }

  private toApiDate(value: any): string | null {
    if (!value) return null;

    if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
      return value;
    }

    if (typeof value === "string" && value.includes("T")) {
      return value.split("T")[0];
    }

    const date = value instanceof Date ? value : new Date(value);

    if (isNaN(date.getTime())) return null;

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  }

  private toFormDate(value: any): string | null {
    return this.toApiDate(value);
  }

  private resolveStaffUserId(value: any): string | null {
    return (
      value?.resolved_user_id ||
      value?.user_id ||
      value?.id ||
      value?.user ||
      null
    );
  }

  private extractArray<T = any>(response: any): T[] {
    if (Array.isArray(response)) return response;
    if (Array.isArray(response?.results)) return response.results;
    if (Array.isArray(response?.data)) return response.data;
    return [];
  }

  private buildWorkingHours(): void {
    this.workingHoursArray.clear();

    this.workingDays.forEach((day) => {
      const row = this.fb.group({
        day_of_week: [day.value],
        day_label: [day.label],
        enabled: [false],
        start_time: [{ value: "09:00", disabled: true }],
        end_time: [{ value: "17:00", disabled: true }],
      });

      row.get("enabled")?.valueChanges.subscribe((enabled) => {
        const start = row.get("start_time");
        const end = row.get("end_time");

        if (enabled) {
          start?.enable({ emitEvent: false });
          end?.enable({ emitEvent: false });
        } else {
          start?.disable({ emitEvent: false });
          end?.disable({ emitEvent: false });
        }
      });

      this.workingHoursArray.push(row);
    });
  }

  private loadSalons(): void {
    this.loading = true;

    this.apiService.request(API_ENDPOINTS.salons.list, "GET").subscribe({
      next: (response: any) => {
        this.salons = this.extractArray<Salon>(response);
        this.loading = false;
      },
      error: (error) => {
        console.error("Error loading salons:", error);
        this.loading = false;
      },
    });
  }

  private loadServicesForSalon(salonId: string): void {
    this.loadingServices = true;

    this.apiService
      .request(API_ENDPOINTS.salons.services.list(salonId), "GET")
      .subscribe({
        next: (response: any) => {
          this.services = this.extractArray<Service>(response);
          this.loadingServices = false;
        },
        error: (error) => {
          console.error("Error loading services:", error);
          this.services = [];
          this.loadingServices = false;
        },
      });
  }

  private loadEmployee(): void {
    this.loading = true;

    this.apiService
      .request<Employee>(API_ENDPOINTS.staff.detail(this.employeeID!), "GET")
      .pipe(
        switchMap((employee: any) => {
          this.populateForm(employee);

          const salonId = employee?.salon || employee?.salon_id;
          const staffUserId =
            this.resolveStaffUserId(employee) || this.employeeID;

          if (!salonId || !staffUserId) {
            return of({ employee, serviceMappings: [], workingHours: [] });
          }

          return forkJoin({
            employee: of(employee),
            serviceMappings: this.apiService
              .request(
                API_ENDPOINTS.staffAvailability.services(salonId, staffUserId),
                "GET",
              )
              .pipe(catchError(() => of([]))),
            workingHours: this.apiService
              .request(
                API_ENDPOINTS.staffAvailability.workingHoursList(
                  salonId,
                  staffUserId,
                ),
                "GET",
              )
              .pipe(catchError(() => of([]))),
          });
        }),
        catchError((err) => {
          this.loading = false;
          console.error(err);
          return of({
            employee: null,
            serviceMappings: [],
            workingHours: [],
          });
        }),
      )
      .subscribe({
        next: ({ employee, serviceMappings, workingHours }: any) => {
          if (employee?.salon || employee?.salon_id) {
            this.loadServicesForSalon(employee?.salon || employee?.salon_id);
          }

          this.populateSelectedServices(this.extractArray(serviceMappings));
          this.populateWorkingHours(this.extractArray(workingHours));
          this.loading = false;
        },
        error: (error) => {
          this.loading = false;
          console.error("Error while loading employee", error);
        },
      });
  }

  private populateForm(employee: any): void {
    const salonId = employee?.salon || employee?.salon_id || "";

    this.employeeForm.patchValue({
      full_name: employee?.full_name,
      salon_id: salonId,

      firstName: employee?.first_name || employee?.firstName || "",
      lastName: employee?.last_name || employee?.lastName || "",
      dateOfBirth: this.toFormDate(
        employee?.date_of_birth || employee?.dateOfBirth,
      ),
      gender: employee?.gender || "",
      maritalStatus: employee?.marital_status || employee?.maritalStatus || "",
      city: employee?.city || "",

      address: employee?.address || "",
      nationality: employee?.nationality || "",
      province: employee?.province || "",
      postalCode: employee?.postal_code || employee?.postalCode || "",
      addressCity: employee?.address_city || employee?.addressCity || "",

      email: employee?.email || employee?.user_email || "",
      phone_number: employee?.phone_number || "",

      job_title: employee?.job_title || "",
      departmentName:
        employee?.department_name || employee?.departmentName || "",
      interviewDate: this.toFormDate(
        employee?.interview_date || employee?.interviewDate,
      ),
      hiringDate: this.toFormDate(employee?.hiring_date || employee?.hire_date),
      designation: employee?.designation || "",
      grossSalary: employee?.gross_salary || employee?.grossSalary || "",
      reportingTo: employee?.reporting_to || employee?.reportingTo || "",
    });
  }

  private populateSelectedServices(mappings: any[]): void {
    const ids = mappings.map((m) => m.service_id || m.service).filter(Boolean);

    this.employeeForm.patchValue({
      selected_service_ids: ids,
    });
  }

  private populateWorkingHours(hours: any[]): void {
    if (!hours?.length) return;

    hours.forEach((hour) => {
      const row = this.workingHoursArray.controls.find(
        (ctrl) => ctrl.get("day_of_week")?.value === hour.day_of_week,
      ) as FormGroup | undefined;

      if (!row) return;

      row.patchValue({
        enabled: !hour.is_off,
        start_time: hour.start_time
          ? String(hour.start_time).slice(0, 5)
          : "09:00",
        end_time: hour.end_time ? String(hour.end_time).slice(0, 5) : "17:00",
      });

      if (!hour.is_off) {
        row.get("start_time")?.enable({ emitEvent: false });
        row.get("end_time")?.enable({ emitEvent: false });
      } else {
        row.get("start_time")?.disable({ emitEvent: false });
        row.get("end_time")?.disable({ emitEvent: false });
      }
    });
  }

  onServiceToggle(serviceId: string, checked: boolean): void {
    const current = [
      ...(this.employeeForm.get("selected_service_ids")?.value || []),
    ];

    if (checked) {
      if (!current.includes(serviceId)) current.push(serviceId);
    } else {
      const idx = current.indexOf(serviceId);
      if (idx > -1) current.splice(idx, 1);
    }

    this.employeeForm.patchValue({
      selected_service_ids: current,
    });

    this.employeeForm.get("selected_service_ids")?.markAsTouched();
  }

  isServiceSelected(serviceId: string): boolean {
    const current = this.employeeForm.get("selected_service_ids")?.value || [];
    return current.includes(serviceId);
  }

  private buildEmployeePayload(): any {
    const formData = this.employeeForm.getRawValue();

    const fullName =
      `${formData.firstName || ""} ${formData.lastName || ""}`.trim();

    return {
      full_name: fullName,
      password: formData.password,
      email: formData.email,
      phone_number:
        formData.phone_number?.e164Number || formData.phone_number || "",

      first_name: formData.firstName,
      last_name: formData.lastName,
      date_of_birth: this.toApiDate(formData.dateOfBirth),
      gender: formData.gender,
      marital_status: formData.maritalStatus,
      city: formData.city,

      address: formData.address,
      nationality: formData.nationality,
      province: formData.province,
      postal_code: formData.postalCode,
      address_city: formData.addressCity,

      job_title: formData.job_title,
      department_name: formData.departmentName,
      interview_date: this.toApiDate(formData.interviewDate),
      hiring_date: this.toApiDate(formData.hiringDate),
      designation: formData.designation,
      gross_salary: formData.grossSalary,
      reporting_to: formData.reportingTo,
    };
  }

  private buildWorkingHoursPayload() {
    return {
      hours: this.workingHoursArray.controls.map((ctrl) => {
        const raw = ctrl.getRawValue();

        const enabled = !!raw.enabled;
        const start = raw.start_time || "09:00";
        const end = raw.end_time || "17:00";

        return {
          day_of_week: raw.day_of_week,
          start_time: enabled ? `${start}:00` : "00:00:00",
          end_time: enabled ? `${end}:00` : "00:00:00",
          is_off: !enabled,
        };
      }),
    };
  }

  private syncStaffServices(
    salonId: string,
    staffUserId: string,
    selectedServiceIds: string[],
  ) {
    return this.apiService
      .request(
        API_ENDPOINTS.staffAvailability.services(salonId, staffUserId),
        "GET",
      )
      .pipe(
        switchMap((existingResponse: any) => {
          const existingList: StaffServiceMapping[] =
            this.extractArray<StaffServiceMapping>(existingResponse);

          const existingServiceIds = existingList.map(
            (x) => x.service_id || x.service,
          );

          const toCreate = selectedServiceIds.filter(
            (id) => !existingServiceIds.includes(id),
          );

          const toDelete = existingList.filter(
            (x) => !selectedServiceIds.includes(x.service_id || x.service),
          );

          const createCalls = toCreate.map((serviceId) =>
            this.apiService.request(
              API_ENDPOINTS.staffAvailability.services(salonId, staffUserId),
              "POST",
              {
                service: serviceId,
                is_active: true,
              },
            ),
          );

          const deleteCalls = toDelete.map((mapping) =>
            this.apiService.request(
              API_ENDPOINTS.staffAvailability.serviceDetail(
                salonId,
                staffUserId,
                mapping.id,
              ),
              "DELETE",
            ),
          );

          if (!createCalls.length && !deleteCalls.length) {
            return of(true);
          }

          return forkJoin([...createCalls, ...deleteCalls]).pipe(
            map(() => true),
          );
        }),
        catchError((err) => {
          console.error("SYNC STAFF SERVICES FAILED", err);
          throw err;
        }),
      );
  }

  private syncWorkingHours(salonId: string, staffUserId: string) {
    const payload = this.buildWorkingHoursPayload();

    return this.apiService
      .request(
        API_ENDPOINTS.staffAvailability.workingHoursUpsert(
          salonId,
          staffUserId,
        ),
        "PUT",
        payload,
      )
      .pipe(
        tap((res) => console.log("WORKING HOURS SAVE RESPONSE", res)),
        catchError((err) => {
          console.error("SYNC WORKING HOURS FAILED", err);
          throw err;
        }),
      );
  }

  onSubmit(): void {
    if (this.employeeForm.invalid) {
      this.employeeForm.markAllAsTouched();
      this.goToFirstInvalidTab();
      return;
    }

    this.loading = true;

    if (this.isEditMode) {
      this.updateEmployee();
    } else {
      this.createEmployee();
    }
  }

  private goToFirstInvalidTab(): void {
    const profileFields = [
      "firstName",
      "lastName",
      "dateOfBirth",
      "gender",
      "maritalStatus",
      "phone_number",
      "email",
      "password",
    ];

    const addressFields = [
      "city",
      "address",
      "nationality",
      "province",
      "postalCode",
      "addressCity",
    ];

    const employmentFields = [
      "job_title",
      "salon_id",
      "departmentName",
      "interviewDate",
      "hiringDate",
      "designation",
      "grossSalary",
      "reportingTo",
    ];

    if (profileFields.some((field) => this.employeeForm.get(field)?.invalid)) {
      this.activeTab = "profile";
      return;
    }

    if (addressFields.some((field) => this.employeeForm.get(field)?.invalid)) {
      this.activeTab = "address";
      return;
    }

    if (
      employmentFields.some((field) => this.employeeForm.get(field)?.invalid)
    ) {
      this.activeTab = "employment";
      return;
    }

    if (this.employeeForm.get("selected_service_ids")?.invalid) {
      this.activeTab = "services";
    }
  }

  private createEmployee(): void {
    const salonId = this.employeeForm.get("salon_id")?.value;
    const employeePayload = this.buildEmployeePayload();
    const selectedServiceIds =
      this.employeeForm.get("selected_service_ids")?.value || [];

    this.apiService
      .request(
        API_ENDPOINTS.staff.createEmployee(salonId),
        "POST",
        employeePayload,
        "Employee Added Successfully!",
      )
      .pipe(
        switchMap((createdEmployee: any) => {
          const staffUserId = this.resolveStaffUserId(createdEmployee);

          if (!staffUserId) {
            throw new Error(
              "Staff user id not returned from create employee endpoint.",
            );
          }

          return forkJoin({
            services: this.syncStaffServices(
              salonId,
              staffUserId,
              selectedServiceIds,
            ),
            workingHours: this.syncWorkingHours(salonId, staffUserId),
          });
        }),
      )
      .subscribe({
        next: () => {
          this.loading = false;
          this.closeDialog();
        },
        error: (error) => {
          console.error("Error creating employee:", error);
          this.loading = false;
        },
      });
  }

  private updateEmployee(): void {
    const salonId = this.employeeForm.get("salon_id")?.value;
    const selectedServiceIds =
      this.employeeForm.get("selected_service_ids")?.value || [];

    const payload = this.buildEmployeePayload();

    if (!payload.password) {
      delete payload.password;
    }

    this.apiService
      .request(
        API_ENDPOINTS.staff.updateEmployee(this.employeeID || ""),
        "PATCH",
        payload,
        "Employee Updated Successfully!",
      )
      .pipe(
        switchMap((updatedEmployee: any) => {
          const staffUserId =
            this.resolveStaffUserId(updatedEmployee) || this.employeeID;

          if (!staffUserId) {
            throw new Error("Staff user id not available for update sync.");
          }

          return forkJoin({
            services: this.syncStaffServices(
              salonId,
              staffUserId,
              selectedServiceIds,
            ),
            workingHours: this.syncWorkingHours(salonId, staffUserId),
          });
        }),
      )
      .subscribe({
        next: () => {
          this.loading = false;
          this.closeDialog();
        },
        error: (error) => {
          console.error("Error updating employee:", error);
          this.loading = false;
        },
      });
  }

  onReset(): void {
    this.employeeForm.reset();
    this.activeTab = "profile";
    this.buildWorkingHours();
  }

  closeDialog(): void {
    if (this.dialogRef) {
      this.dialogRef.close(true);
      return;
    }

    this.router.navigate(["/admin/business/employee-management"]);
  }
}
